import { memo, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import ShowInApplicationLink from "@/components/application/aiAnalysis/ShowInApplicationLink";
import BaseTransactionTable, {
  type TableColumn,
} from "@/components/transactions/BaseTransactionTable";
import ExpandableRow from "@/components/transactions/ExpandableRow";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { CorrectionItem } from "@/types/analysisRun";

import { PhoneSheet, useIsPhone } from "../chat/PhoneSheet";
import { useOpenedBy } from "../hooks/useOpenedBy";
import type { AgentState } from "../store/agentSlice";
import { selectChoice } from "../store/selectors";
import { AssistantCard, AssistantCardBody, AssistantCardHeader } from "../theme";
import { THREAD_CARD } from "./ApprovalCard";
import { CARD_TABLE, cardCells, cardColumns, ChangeStack, changeLabel } from "./cardTable";
import {
  DecisionFooter,
  FIGURES_FILL_ROW,
  ImpactSection,
  impactScope,
  LOOKS,
  RowMark,
  TickBox,
  useCorrectionImpact,
  useDecide,
  useTicks,
} from "./correctionCard";
import { FigureGrid } from "./FigureGrid";
import { ReclassifyMonthCredits } from "./ReclassifyMonthCredits";
import { CATEGORY_NAMES, moves, NONE, REVENUE_NAMES } from "./transactionChange";
import { choiceOutcome, transactionCard, type TransactionRow } from "./wire";

const cents = (value: string, count = 1) => Math.round(Number(value || 0) * 100) * count;
const money = (amountCents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amountCents / 100);

/** The corrections the ticked rows make, as the preview endpoint takes them. */
function correctionItems(rows: TransactionRow[]): CorrectionItem[] {
  return rows.flatMap(({ transaction }) =>
    [
      ...(transaction.category.old !== transaction.category.new
        ? [{ field: "txn_type", value: transaction.category.new }]
        : []),
      ...(transaction.revenue.old !== transaction.revenue.new
        ? [{ field: "rev", value: transaction.revenue.new }]
        : []),
    ].map((change) => ({
      target_type: "transaction" as const,
      target_key: transaction.override_key,
      ...change,
    }))
  );
}

/** A column that changes: its heading carries the legend's dot. */
function Changing({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary" />
      {label}
    </span>
  );
}

/**
 * An approval whose rows are transactions (reclassify_transactions): the analysis's top row as
 * it would be, over what it was, and the transactions as the AI Analysis tab's table, each
 * ticked. Ticking fewer asks the server what those alone would change; approving sends the
 * ticked rows. The web's own card: the MCP approval view draws the same blocks as plain HTML.
 */
export const ReclassifyApprovalCard = memo(function ReclassifyApprovalCard({
  choiceRef,
}: {
  choiceRef: string;
}) {
  const choice = useSelector((state: { agent: AgentState }) => selectChoice(state, choiceRef));
  const card = useMemo(() => (choice ? transactionCard(choice) : null), [choice]);
  const phone = useIsPhone();
  const [expanded, setExpanded] = useState<string | null>(null);
  // The Recent Revenue tile opened to show its month's credits.
  const [month, setMonth] = useState<string | null>(null);
  const monthKeys = useOpenedBy();
  const { pressed, busy, error, setError, press } = useDecide(choiceRef);

  const outcome = choice ? choiceOutcome(choice) : "superseded";
  const pending = outcome === "pending";
  const rows = useMemo(() => card?.rows ?? [], [card]);
  const { proposed, offered, ticked, narrowed, toggle, setTicks } = useTicks(rows, pending);
  const chosen = rows.filter((row) => ticked.has(row.key));
  // One string for the ticked set, so the preview's items keep their identity between renders
  // (the debounce restarts on a new array).
  const tickedKey = narrowed ? chosen.map((row) => row.key).join(",") : "";
  const items = useMemo(
    () =>
      tickedKey
        ? correctionItems(rows.filter((row) => tickedKey.split(",").includes(row.key)))
        : [],
    [tickedKey, rows]
  );
  const { impact, failed, updating } = useCorrectionImpact(card, {
    narrowed,
    nothingTicked: !chosen.length,
    items,
  });
  if (!choice || !card) return null;

  const look = LOOKS[outcome];
  const tickable = pending && !busy;
  const scope = impactScope({
    outcome,
    narrowed,
    failed,
    chosen: chosen.length,
    proposed: proposed.length,
    offered: rows.length,
    stats: card.stats,
  });

  const tick = (key: string) => {
    toggle(key);
    setError(null);
  };
  const allTicked = chosen.length === offered.length;
  const toggleAll = () => {
    setTicks(new Set(allTicked ? [] : offered.map((row) => row.key)));
    setError(null);
  };

  const categoryMoves = rows.some((row) => moves(row.transaction.category));
  const revenueMoves = rows.some((row) => moves(row.transaction.revenue));
  const columns: TableColumn<TransactionRow>[] = [
    {
      label: "Select",
      width: 28,
      header: tickable ? (
        <TickBox
          checked={allTicked ? true : chosen.length ? "indeterminate" : false}
          onChange={toggleAll}
          label="All transactions"
        />
      ) : (
        <span className="sr-only">Selected</span>
      ),
    },
    // The dot and its legend say what would change, so only while it still can.
    ...cardColumns<TransactionRow>(phone, {
      label: changeLabel(categoryMoves, revenueMoves),
      ...(pending && { header: <Changing label={changeLabel(categoryMoves, revenueMoves)} /> }),
    }),
  ];

  const renderRow = (row: TransactionRow) => {
    const { transaction } = row;
    const on = ticked.has(row.key);
    const settled = outcome === "approved" && on ? row.state : null;
    const cells = cardCells(
      phone,
      transaction,
      <ChangeStack
        category={categoryMoves ? transaction.category : undefined}
        revenue={revenueMoves ? transaction.revenue : undefined}
      />
    );
    return (
      <ExpandableRow
        key={row.key}
        isExpanded={expanded === row.key}
        onToggle={() => setExpanded((current) => (current === row.key ? null : row.key))}
        cells={cells}
        detail={{
          type: "fields",
          fields: [
            { label: "Description:", value: transaction.description || NONE },
            { label: "Memo:", value: transaction.memo || NONE },
            {
              label: "Account:",
              value: transaction.account_last4 ? `…${transaction.account_last4}` : NONE,
            },
            ...(categoryMoves
              ? []
              : [
                  {
                    label: "Category:",
                    value:
                      CATEGORY_NAMES[transaction.category.new] ??
                      (transaction.category.new || NONE),
                  },
                ]),
            ...(revenueMoves
              ? []
              : [{ label: "Revenue:", value: REVENUE_NAMES[transaction.revenue.new] ?? NONE }]),
            ...(transaction.count > 1
              ? [{ label: "Identical:", value: `${transaction.count} transactions, all changed` }]
              : []),
            ...(transaction.counterparty
              ? [{ label: "Counterparty:", value: transaction.counterparty }]
              : []),
            ...(pending && row.disabled ? [{ label: "Can't tick:", value: row.disabled }] : []),
            ...(settled && row.line ? [{ label: "Result:", value: row.line }] : []),
          ],
        }}
        compact
        leading={
          <RowMark
            state={settled}
            checked={on}
            onChange={() => tick(row.key)}
            label={row.disabled ? `${row.label}, ${row.disabled}` : row.label}
            disabled={!tickable || !!row.disabled}
          />
        }
        className={cn(!on && "[&>td:not(:first-child)]:opacity-50")}
      />
    );
  };

  const selectedCents = chosen.reduce(
    (sum, row) => sum + cents(row.transaction.amount, row.transaction.count),
    0
  );
  const credits = (onPhone: boolean) =>
    month !== null && (
      <ReclassifyMonthCredits
        card={card}
        month={month}
        ticked={chosen}
        phone={onPhone}
        onClose={() => setMonth(null)}
      />
    );

  return (
    <AssistantCard className={THREAD_CARD} data-outcome={outcome} data-choice-ref={choiceRef}>
      <AssistantCardHeader
        className="items-start"
        icon={look.icon}
        tone={look.tone}
        title={<span className="whitespace-normal break-words">{choice.prompt}</span>}
      />
      <AssistantCardBody className="flex flex-col gap-3">
        {choice.summary && <p className="text-muted-foreground">{choice.summary}</p>}
        {card.figures.length > 0 && (
          <FigureGrid figures={card.figures} className={FIGURES_FILL_ROW} />
        )}
        <ImpactSection
          group={card.stats?.group}
          impact={impact}
          scope={scope}
          updating={updating}
          dimmed={outcome === "declined" || outcome === "superseded"}
          hideDecision={narrowed && !chosen.length}
          failedNote={
            failed
              ? `Couldn't work out the figures for the selected rows; showing them for all ${proposed.length}.`
              : null
          }
          months={{
            active: month,
            onClick: (abbrev) => setMonth((open) => (open === abbrev ? null : abbrev)),
          }}
          wrapStrip={(strip) => (
            <>
              <Popover
                open={month !== null && !phone}
                onOpenChange={(open) => !open && setMonth(null)}
              >
                <PopoverAnchor asChild>
                  {/* The month tiles' presses reach here: a tapped one's sheet takes no ring. */}
                  <div {...monthKeys.triggerProps}>{strip}</div>
                </PopoverAnchor>
                {month !== null && !phone && (
                  <PopoverContent
                    align="start"
                    collisionPadding={8}
                    className="flex max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] flex-col p-3"
                  >
                    {credits(false)}
                  </PopoverContent>
                )}
              </Popover>
              {phone && (
                <PhoneSheet
                  open={month !== null}
                  onOpenChange={(open) => !open && setMonth(null)}
                  title={`${month ?? ""} credits`}
                  openedBy={monthKeys.openedBy}
                  openedFrom={monthKeys.openedFrom}
                >
                  {credits(true)}
                </PhoneSheet>
              )}
            </>
          )}
        />
        <section aria-label="Transactions" className="flex min-w-0 flex-col gap-1.5">
          <div className={CARD_TABLE}>
            <BaseTransactionTable
              dense
              columns={columns}
              items={rows}
              renderRow={renderRow}
              defaultRowsPerPage={20}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-3">
              <span>
                {chosen.length} of {offered.length} selected · {money(selectedCents)}
              </span>
              {pending && (
                <span className="inline-flex items-center gap-1">
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Changing
                </span>
              )}
            </span>
            <ShowInApplicationLink
              target={{
                applicationId: card.applicationId,
                run: card.runId,
                filter: {
                  kind: "keys",
                  keys: rows.map((row) => row.transaction.override_key),
                },
                highlight: chosen.map((row) => row.transaction.override_key),
              }}
            />
          </div>
        </section>
        {error && (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </AssistantCardBody>
      <DecisionFooter
        choice={choice}
        outcome={outcome}
        pressed={pressed}
        chosen={chosen.length}
        narrowed={narrowed}
        onPress={(optionId) => press(optionId, narrowed ? chosen.map((row) => row.key) : undefined)}
      />
    </AssistantCard>
  );
});
